import uniq from 'lodash/uniq';
import { FetchBaseQueryError } from '@reduxjs/toolkit/dist/query';
import { QueryReturnValue } from '@reduxjs/toolkit/dist/query/baseQueryTypes';

import { serverApi } from '../index';

// types
import { ForgotPasswordReq, LoginReq, LoginRes, ResetForgotPasswordReq, ChangePasswordReq, LogoutRes } from 'types/login';
import { AnalyticsAuthEvents, setUser as setAnalyticsUser, trackAnalyticsEvent } from '../../../../utils/analytics';
import { UserRole } from '../../../../types/auth';

const handleResult = (result: QueryReturnValue<unknown, FetchBaseQueryError, {}>) => {
  const customErrorData = {
    error: 'Unauthorized',
    message: 'Username or password is not valid',
    statusCode: 401
  };

  const loginRes = result.data as LoginRes;

  if (loginRes) {
    if (loginRes.mfa_enabled) {
      return { data: loginRes };
    }

    const role = loginRes.roles?.[0];

    if (!role) {
      return {
        error: {
          data: customErrorData,
          status: 401
        } as FetchBaseQueryError
      };
    }

    const { user_role_type_id } = role;

    if (user_role_type_id > 7 && ![11, 12, 13].includes(user_role_type_id)) {
      return {
        error: {
          data: customErrorData,
          status: 401
        } as FetchBaseQueryError
      };
    }
    const companies = getCompaniesFromRole(loginRes.roles || []);
    setAnalyticsUser(`${loginRes.user.id}`, companies, loginRes.user.email, `${loginRes.user.first_name} ${loginRes.user.last_name}`);
    return { data: loginRes };
  } else {
    return { error: result.error as FetchBaseQueryError };
  }
};

const getCompaniesFromRole = (roles: UserRole[]): string[] => {
  const companies: string[] = [];
  roles.forEach((r) => {
    companies.push(`${r.company_id || 'admin'}`);
  });
  return uniq(companies);
};

export const authApi = serverApi.injectEndpoints({
  endpoints: (builder) => ({
    login: builder.mutation<LoginRes, LoginReq>({
      queryFn: async (body, _queryApi, _extraOptions, fetchWithBQ) => {
        const result = await fetchWithBQ({ url: '/auth/login', body, method: 'POST' });
        const loginResult = result.data as LoginRes;
        if (loginResult && !loginResult.mfa_enabled) {
          trackAnalyticsEvent(AnalyticsAuthEvents.LOG_IN, { email: loginResult.user.email });
        }
        return handleResult(result);
      }
    }),
    loginWithMfa: builder.mutation<LoginRes, LoginReq>({
      queryFn: async (body, _queryApi, _extraOptions, fetchWithBQ) => {
        const result = await fetchWithBQ({ url: '/auth/login-mfa', body, method: 'POST' });
        const loginResult = result.data as LoginRes;
        if (loginResult) {
          trackAnalyticsEvent(AnalyticsAuthEvents.LOG_IN, { email: loginResult.user.email });
        }
        return handleResult(result);
      }
    }),
    logout: builder.mutation<LogoutRes, void>({
      queryFn: async (body, _queryApi, _extraOptions, fetchWithBQ) => {
        const result = await fetchWithBQ({ url: '/auth/logout', body, method: 'POST' });
        const logoutResult = result.data as LogoutRes;
        if (logoutResult?.success) {
          trackAnalyticsEvent(AnalyticsAuthEvents.LOG_OUT);
        }
        return { data: logoutResult };
      }
    }),
    me: builder.query<Omit<LoginRes, 'access_token'>, void>({
      queryFn: async (_args, _queryApi, _extraOptions, fetchWithBQ) => {
        const result = await fetchWithBQ({ url: '/auth/me' });
        return handleResult(result);
      }
    }),
    getNewToken: builder.query<Partial<LoginRes>, { role_id: number }>({
      queryFn: async (_args, _queryApi, _extraOptions, fetchWithBQ) => {
        const result = await fetchWithBQ({ url: `/auth/new-token/${_args.role_id}` });
        return handleResult(result);
      }
    }),
    sendForgotPasswordEmail: builder.mutation<void, ForgotPasswordReq>({
      query: (body) => ({ url: '/auth/forgot-password-email', body, method: 'POST' })
    }),
    resetForgotPassword: builder.mutation<void, ResetForgotPasswordReq>({
      query: (body) => ({ url: '/auth/forgot-password-reset', body, method: 'POST' })
    }),
    changePassword: builder.mutation<void, ChangePasswordReq>({
      query: (body) => ({ url: '/auth/change-password', body, method: 'POST' })
    })
  }),
  overrideExisting: false
});

export const {
  useLoginMutation,
  useLogoutMutation,
  useMeQuery,
  useLazyMeQuery,
  useLazyGetNewTokenQuery,
  useResetForgotPasswordMutation,
  useSendForgotPasswordEmailMutation,
  useChangePasswordMutation,
  useLoginWithMfaMutation
} = authApi;
